import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  CheckCircle,
  XCircle,
  Loader2,
  ArrowRight,
  Home,
  RefreshCw
} from "lucide-react";

export function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");
  const maxAttempts = 10;
  const pollInterval = 2000;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollAttempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await authFetch(`${API}/payments/status/${sessionId}`);
      
      if (response.ok) {
        const data = await response.json();
        setPaymentData(data);
        
        if (data.payment_status === "paid") {
          setStatus("success");
          toast.success("Payment successful!");
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        
        // Continue polling if still pending
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus("error");
      }
    } catch (_error) {
      console.error("Error checking status:", _error);
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const getNextAction = () => {
    if (!paymentData?.product_id) return { label: "Go to Dashboard", path: "/dashboard" };
    
    switch (paymentData.product_id) {
      case "rapid":
        return { label: "Start R.A.P.I.D. Program", path: "/rapid" };
      case "podcast_academy":
        return { label: "Start Podcast Academy", path: "/podcast" };
      case "podcast_production":
        return { label: "Schedule Session", path: "/dashboard" };
      default:
        return { label: "Go to Dashboard", path: "/dashboard" };
    }
  };

  const nextAction = getNextAction();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-white/5 border-white/10">
        <CardContent className="pt-8 pb-8 text-center">
          {/* Checking Status */}
          {status === "checking" && (
            <>
              <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
              <p className="text-white/60 mb-4">Please wait while we confirm your payment...</p>
              <div className="flex justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full ${
                      i <= pollAttempts % 5 ? "bg-purple-400" : "bg-white/20"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Success */}
          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-white/60 mb-2">Thank you for your purchase</p>
              
              {paymentData && (
                <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
                  <p className="text-white font-medium">{paymentData.product_name}</p>
                  <p className="text-2xl font-bold text-green-400">
                    ${paymentData.amount?.toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => navigate(nextAction.path)}
                  data-testid="next-action-btn"
                >
                  {nextAction.label}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                  onClick={() => navigate("/dashboard")}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {/* Error */}
          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Error</h1>
              <p className="text-white/60 mb-6">
                We could not verify your payment. Please contact support if you were charged.
              </p>
              <Button
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
                onClick={() => navigate("/pricing")}
              >
                Return to Pricing
              </Button>
            </>
          )}

          {/* Timeout */}
          {status === "timeout" && (
            <>
              <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <RefreshCw className="w-8 h-8 text-yellow-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Taking Longer Than Expected</h1>
              <p className="text-white/60 mb-6">
                Your payment is being processed. You will receive an email confirmation shortly.
              </p>
              <div className="space-y-3">
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={() => {
                    setPollAttempts(0);
                    setStatus("checking");
                    pollPaymentStatus();
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Check Again
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                  onClick={() => navigate("/dashboard")}
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {/* Expired */}
          {status === "expired" && (
            <>
              <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-orange-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Session Expired</h1>
              <p className="text-white/60 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={() => navigate("/pricing")}
              >
                Return to Pricing
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PaymentSuccessPage;
